<template>
  <div class="relative max-w-2xl mx-auto">
    <!-- Combined Search Bar -->
    <div class="relative group">
      <div class="absolute inset-0 bg-gradient-to-r from-secondary-500/10 to-primary-500/10 rounded-lg blur opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
      <div 
        class="relative flex items-center bg-white/80 backdrop-blur-sm border border-gray-200 rounded-xl shadow-lg dark:bg-gray-800/90 dark:border-gray-700/50 overflow-visible"
      >
        <!-- Location Input Container -->
        <div class="relative flex-shrink-0 md:min-w-[200px] min-w-[140px]" ref="locationContainerRef">
          <!-- Location Input -->
          <div ref="locationInputRef" class="flex items-center relative">
            <div 
              class="absolute inset-y-0 flex items-center pointer-events-none"
              :class="[isRTLLanguage ? 'right-0 md:pr-4 pr-2' : 'left-0 md:pl-4 pl-2']"
            >
              <svg class="w-5 h-5 text-gray-400 transition-colors duration-200 group-hover:text-secondary-500 dark:group-hover:text-secondary-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                <path fill-rule="evenodd" d="M9.69 18.933l.003.001C9.89 19.02 10 19 10 19s.11.02.308-.066l.002-.001.006-.003.018-.008a5.741 5.741 0 00.281-.14c.186-.096.446-.24.757-.433.62-.384 1.445-.966 2.274-1.765C15.302 14.988 17 12.493 17 9A7 7 0 103 9c0 3.492 1.698 5.988 3.355 7.584a13.731 13.731 0 002.273 1.765 11.842 11.842 0 00.976.544l.062.029.018.008.006.003zM10 11.25a2.25 2.25 0 100-4.5 2.25 2.25 0 000 4.5z" clip-rule="evenodd" />
              </svg>
            </div>
            <input
              type="text"
              v-model="locationQuery"
              :placeholder="t('search.location.placeholder')"
              :class="[
                'md:w-48 w-32 md:px-4 px-2 py-3 text-gray-900 placeholder-gray-500 bg-transparent dark:text-white dark:placeholder-gray-400 focus:outline-none focus:ring-0 border-0 transition-all duration-300',
                isRTLLanguage 
                  ? 'pl-4 md:pr-12 pr-8 text-right' 
                  : 'md:pl-12 pl-8 pr-4 text-left'
              ]"
              @focus="updateLocationSuggestions(true)"
              @blur="handleLocationBlur"
            />
            <div v-if="selectedLocation" class="flex items-center" :class="[isRTLLanguage ? 'pl-2' : 'pr-2']">
              <button
                @click="clearLocation"
                class="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors duration-200"
              >
                <svg class="w-4 h-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                  <path d="M6.28 5.22a.75.75 0 00-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 101.06 1.06L10 11.06l3.72 3.72a.75.75 0 101.06-1.06L11.06 10l3.72-3.72a.75.75 0 00-1.06-1.06L10 8.94 6.28 5.22z" />
                </svg>
              </button>
            </div>
          </div>
          
          <!-- Location Suggestions Dropdown (Teleported to body) -->
          <Teleport to="body">
            <div 
              v-if="showLocationSuggestions && filteredLocations.length > 0"
              class="fixed w-72 bg-white/95 backdrop-blur-sm rounded-xl shadow-xl dark:bg-gray-800/95 border border-gray-100 dark:border-gray-700/50 overflow-hidden z-[9999999]"
              :style="getLocationDropdownPosition()"
              :dir="isRTLLanguage ? 'rtl' : 'ltr'"
            >
              <ul class="py-2 max-h-[320px] overflow-y-auto">
                <li
                  v-for="location in filteredLocations"
                  :key="location.id"
                  @mousedown="selectLocation(location)"
                  class="px-4 py-2 hover:bg-gradient-to-r hover:from-secondary-50 hover:to-primary-50 dark:hover:from-gray-800 dark:hover:to-gray-800/50 cursor-pointer transition-all duration-300"
                >
                  <span :class="{ 'font-[Vazirmatn] text-right block w-full': isRTLLanguage }">{{ location.name }}</span>
                </li>
              </ul>
            </div>
          </Teleport>
        </div>

        <!-- Divider -->
        <div class="w-px h-6 bg-gray-200 dark:bg-gray-700/50 mx-2"></div>

        <!-- Search Input -->
        <div class="relative flex-1 flex items-center" ref="searchContainerRef">
          <div 
            class="absolute inset-y-0 flex items-center pointer-events-none"
            :class="[isRTLLanguage ? 'right-0 md:pr-4 pr-2' : 'left-0 md:pl-4 pl-2']"
          >
            <svg 
              class="w-5 h-5 text-gray-400 transition-colors duration-200 group-hover:text-secondary-500 dark:group-hover:text-secondary-400" 
              xmlns="http://www.w3.org/2000/svg" 
              viewBox="0 0 20 20" 
              fill="currentColor"
            >
              <path fill-rule="evenodd" d="M9 3.5a5.5 5.5 0 100 11 5.5 5.5 0 000-11zM2 9a7 7 0 1112.452 4.391l3.328 3.329a.75.75 0 11-1.06 1.06l-3.329-3.328A7 7 0 012 9z" clip-rule="evenodd" />
            </svg>
          </div>
          <div ref="searchInputRef" class="w-full">
            <input
              type="text"
              v-model="searchQuery"
              :placeholder="t('home.search.placeholder')"
              :class="[
                'w-full md:px-4 px-2 py-3 text-gray-900 placeholder-gray-500 bg-transparent dark:text-white dark:placeholder-gray-400 focus:outline-none focus:ring-0 border-0 transition-all duration-300',
                isRTLLanguage 
                  ? 'pl-4 md:pr-12 pr-8 text-right' 
                  : 'md:pl-12 pl-8 pr-4 text-left'
              ]"
              @focus="showSuggestions = true"
              @blur="handleBlur"
            />
          </div>
          
          <!-- Search Suggestions Dropdown (Teleported to body) -->
          <Teleport to="body">
            <div
              v-if="showSuggestions && filteredSuggestions.length > 0"
              class="fixed w-72 bg-white/95 backdrop-blur-sm rounded-xl shadow-xl dark:bg-gray-800/95 border border-gray-100 dark:border-gray-700/50 overflow-hidden z-[9999999]"
              :style="getSearchDropdownPosition()"
              :dir="isRTLLanguage ? 'rtl' : 'ltr'"
            >
              <ul class="py-2 max-h-[320px] overflow-y-auto">
                <li
                  v-for="suggestion in filteredSuggestions"
                  :key="suggestion.object_id"
                  @mousedown="handleSuggestionClick(suggestion)"
                  class="px-4 py-2 hover:bg-gradient-to-r hover:from-secondary-50 hover:to-primary-50 dark:hover:from-gray-800 dark:hover:to-gray-800/50 cursor-pointer transition-all duration-300"
                >
                  <div class="flex items-center" :class="{ 'flex-row-reverse': isRTLLanguage }">
                    <!-- Icon based on suggestion type -->
                    <span :class="isRTLLanguage ? 'ml-2' : 'mr-2'" class="text-gray-500" v-if="suggestion.type === 'category'">
                      <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                        <path d="M7 3a1 1 0 000 2h6a1 1 0 100-2H7zM4 7a1 1 0 011-1h10a1 1 0 110 2H5a1 1 0 01-1-1zM2 11a2 2 0 012-2h12a2 2 0 012 2v4a2 2 0 01-2 2H4a2 2 0 01-2-2v-4z" />
                      </svg>
                    </span>
                    <span :class="isRTLLanguage ? 'ml-2' : 'mr-2'" class="text-gray-500" v-else-if="suggestion.type === 'listing'">
                      <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                        <path d="M11 17a1 1 0 001.447.894l4-2A1 1 0 0017 15V9.236a1 1 0 00-1.447-.894l-4 2a1 1 0 00-.553.894V17zM15.211 6.276a1 1 0 000-1.788l-4.764-2.382a1 1 0 00-.894 0L4.789 4.488a1 1 0 000 1.788l4.764 2.382a1 1 0 00.894 0l4.764-2.382zM4.447 8.342A1 1 0 003 9.236V15a1 1 0 00.553.894l4 2A1 1 0 009 17v-5.764a1 1 0 00-.553-.894l-4-2z" />
                      </svg>
                    </span>
                    <span :class="{ 'font-[Vazirmatn] text-right block w-full': isRTLLanguage }">
                      {{ suggestion.text }}
                    </span>
                  </div>
                </li>
              </ul>
            </div>
          </Teleport>
        </div>
      
        <!-- Loading Indicator -->
        <div 
          v-if="isLoading" 
          class="flex items-center"
          :class="[isRTLLanguage ? 'pl-4' : 'pr-4']"
        >
          <div class="animate-spin rounded-full h-5 w-5 border-2 border-secondary-500 border-t-transparent"></div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted, onUnmounted, type Ref } from 'vue'
import { debounce } from 'lodash'
import { useRouter } from 'vue-router'
import { useTranslation } from '@/composables/useTranslation'
import { useListingsStore } from '@/store/modules/listings'
import { useUiStore } from '@/store/modules/ui'
import { isRTL } from '@/utils/text'
import api from '@/api/client'
import { getCookie, setCookie, deleteCookie } from '@/utils/cookies'
import eventBus from '@/utils/eventBus'

import { useLocations } from '@/composables/useLocations'
import type { Location } from '@/composables/useLocations'

// Define the SearchSuggestion type to match the backend response
interface SearchSuggestion {
  text: string
  type: string
  object_id: string
  id?: string
  image_urls?: string[]
  images?: string[]
}

const { t } = useTranslation()
const { locations, loading: locationsLoading, fetchLocations } = useLocations()
const router = useRouter()
const listingsStore = useListingsStore()

const searchQuery = ref('')
const locationQuery = ref('')
const selectedLocation = ref<Location | null>(null)
const showSuggestions = ref(false)
const showLocationSuggestions = ref(false)
const isLoading = ref(false)
const searchResults = ref<SearchSuggestion[]>([])

// Get language from UI store
const uiStore = useUiStore()
const currentLang = ref(uiStore.language)

// Refs for input elements
const locationInputRef = ref<HTMLElement | null>(null)
const searchInputRef = ref<HTMLElement | null>(null)
const locationContainerRef = ref<HTMLElement | null>(null)
const searchContainerRef = ref<HTMLElement | null>(null)

// Define the dropdown style type
interface DropdownStyle {
  top: string;
  left?: string;
  right?: string;
  zIndex: number;
}

// Dropdown position styles
const locationDropdownStyle = ref<DropdownStyle>({
  top: '0px',
  left: '0px',
  zIndex: 9999999,
})

const searchDropdownStyle = ref<DropdownStyle>({
  top: '0px',
  left: '0px',
  zIndex: 9999999,
})

// Computed property to check if the current language is RTL
const isRTLLanguage = computed(() => {
  return currentLang.value === 'fa' || currentLang.value === 'ar'
})

// Watch for language changes in localStorage
window.addEventListener('storage', (event) => {
  if (event.key === 'language') {
    currentLang.value = event.newValue as any || 'en'
  }
})

// Update language from UI store
watch(() => uiStore.language, (newLang) => {
  currentLang.value = newLang
})

// Function to calculate search dropdown position for teleported element
const getSearchDropdownPosition = () => {
  if (!searchContainerRef.value) return { top: '0px', left: '0px' }
  
  const rect = searchContainerRef.value.getBoundingClientRect()
  
  if (isRTLLanguage.value) {
    // For RTL languages, align with the right edge of the container
    return {
      top: `${rect.bottom + 4}px`,
      right: `${window.innerWidth - rect.right}px`,
      maxWidth: `${rect.width}px`
    }
  } else {
    // For LTR languages, align with the left edge of the container
    return {
      top: `${rect.bottom + 4}px`,
      left: `${rect.left}px`,
      maxWidth: `${rect.width}px`
    }
  }
}

// Function to calculate location dropdown position for teleported element
const getLocationDropdownPosition = () => {
  if (!locationContainerRef.value) return { top: '0px', left: '0px' }
  
  const rect = locationContainerRef.value.getBoundingClientRect()
  
  if (isRTLLanguage.value) {
    // For RTL languages, align with the right edge of the container
    return {
      top: `${rect.bottom + 4}px`,
      right: `${window.innerWidth - rect.right}px`,
      maxWidth: `${rect.width}px`
    }
  } else {
    // For LTR languages, align with the left edge of the container
    return {
      top: `${rect.bottom + 4}px`,
      left: `${rect.left}px`,
      maxWidth: `${rect.width}px`
    }
  }
}

// Function to calculate dropdown position style
const dropdownPositionStyle = (inputRef: any) => {
  if (!inputRef.value) return { top: '0px', left: '0px' }
  
  const rect = inputRef.value.getBoundingClientRect()
  
  if (isRTLLanguage.value) {
    // For RTL languages, align with the right edge of the input
    return {
      top: `${rect.bottom + 4}px`,
      right: `${document.documentElement.clientWidth - rect.right}px`
    }
  } else {
    // For LTR languages, align with the left edge of the input
    return {
      top: `${rect.bottom + 4}px`,
      left: `${rect.left}px`
    }
  }
}

// Update dropdown positions when inputs are focused
const updateDropdownPositions = () => {
  if (locationInputRef.value) {
    const rect = locationInputRef.value.getBoundingClientRect()
    locationDropdownStyle.value = {
      top: `${rect.bottom + 4}px`,
      ...(isRTLLanguage.value 
        ? { right: `${document.documentElement.clientWidth - rect.right}px` } 
        : { left: `${rect.left}px` }),
      zIndex: 9999999,
    }
  }
  
  if (searchInputRef.value) {
    const rect = searchInputRef.value.getBoundingClientRect()
    searchDropdownStyle.value = {
      top: `${rect.bottom + 4}px`,
      ...(isRTLLanguage.value 
        ? { right: `${document.documentElement.clientWidth - rect.right}px` } 
        : { left: `${rect.left}px` }),
      zIndex: 9999999,
    }
  }
}

// Update location suggestions visibility
const updateLocationSuggestions = (show: boolean) => {
  showLocationSuggestions.value = show
  if (show) {
    updateDropdownPositions()
  }
}

// Location handling
const filteredLocations = computed(() => {
  if (!locationQuery.value || !locationQuery.value.trim()) return locations.value
  const query = locationQuery.value.toLowerCase().trim()
  return locations.value.filter(location => 
    location.name.toLowerCase().includes(query)
  )
})

const selectLocation = (location: Location) => {
  selectedLocation.value = location
  locationQuery.value = location.name
  showLocationSuggestions.value = false
  
  // Update the cookie with the selected city ID
  console.log('Setting selected_city cookie to:', location.id.toString())
  setCookie('selected_city', location.id.toString())
}

const clearLocation = () => {
  selectedLocation.value = null
  locationQuery.value = ''
  
  // Delete the cookie when the user clears the location
  console.log('Deleting selected_city cookie')
  deleteCookie('selected_city')
}

const handleLocationBlur = () => {
  // Use a longer delay to ensure click events on suggestions work
  setTimeout(() => {
    if (selectedLocation.value) {
      locationQuery.value = selectedLocation.value.name
    }
    showLocationSuggestions.value = false
  }, 250)
}

// Helper function to get the correct image URL
const getImageUrl = (listing: SearchSuggestion) => {
  const imageUrl = listing.image_urls?.[0] || listing.images?.[0]
  if (!imageUrl) return null
  return imageUrl.startsWith('/') ? imageUrl : `/${imageUrl}`
}

// Computed property to check if text is RTL
const isTextRTL = computed(() => {
  return (text: string) => isRTL(text)
})

// Computed property for filtered suggestions
const filteredSuggestions = computed(() => {
  if (!searchQuery.value) return []
  return searchResults.value
})

// Debounced search function
const debouncedSearch = debounce(async (query: string) => {
  if (!query && !selectedLocation.value) {
    searchResults.value = []
    return
  }
  if (!query) {
    searchResults.value = []
    return
  }
  
  isLoading.value = true
  try {
    const response = await api.get<{ success: boolean; data: SearchSuggestion[] }>('/listings/search/suggestions', {
      params: {
        q: query,
        location_id: selectedLocation.value?.id,
        lang: currentLang.value
      }
    })
    
    console.log('Search suggestions response:', response.data)
    
    // The API response might have a different structure than expected
    // Let's check both possible formats
    if (response.data?.success && Array.isArray(response.data.data)) {
      // Standard format: { success: true, data: [...] }
      searchResults.value = response.data.data || []
    } else if (Array.isArray(response.data)) {
      // Alternative format: direct array
      searchResults.value = response.data
    } else if (response.data && typeof response.data === 'object') {
      // Another possible format: { data: [...] }
      searchResults.value = response.data.data || []
    } else {
      // Fallback
      searchResults.value = []
    }
    
    console.log('Processed search results:', searchResults.value)
  } catch (error) {
    console.error('Error fetching search results:', error)
    searchResults.value = []
  } finally {
    isLoading.value = false
  }
}, 300)

// Watch for search query, location, and language changes
watch([searchQuery, selectedLocation, currentLang], ([newQuery]) => {
  debouncedSearch(newQuery)
})

// Fetch locations on mount and set selected location from cookie
onMounted(async () => {
  await fetchLocations()
  
  // Check for selected city in cookie
  const selectedCityId = getCookie('selected_city')
  console.log('Selected city from cookie:', selectedCityId)
  
  if (selectedCityId) {
    // Find the location in the locations array
    // Note: The cookie value is a string, but location.id is a number
    const cityLocation = locations.value.find(loc => loc.id.toString() === selectedCityId)
    if (cityLocation) {
      console.log('Found city in locations:', cityLocation)
      selectedLocation.value = cityLocation
      locationQuery.value = cityLocation.name
    }
  }
  
  // Listen for city selection events from CitySelectionModal
  eventBus.on('city-selected', (city: any) => {
    console.log('City selected event received:', city)
    
    // If city is "all", clear the location
    if (city.id === 'all') {
      selectedLocation.value = null
      locationQuery.value = ''
      return
    }
    
    // Otherwise, set the selected location
    // First check if the city exists in our locations list
    const cityLocation = locations.value.find(loc => loc.id.toString() === city.id)
    if (cityLocation) {
      selectedLocation.value = cityLocation
      locationQuery.value = cityLocation.name
    } else {
      // If not found in our list, use the city object directly
      selectedLocation.value = city as Location
      locationQuery.value = city.name
    }
    
    // Trigger a search with the new location
    if (searchQuery.value) {
      debouncedSearch(searchQuery.value)
    }
  })

  // Add window resize event listener to update dropdown positions
  window.addEventListener('resize', updateDropdownPositions)
})

// Clean up event listeners on unmount
onUnmounted(() => {
  eventBus.off('city-selected', () => {})
  window.removeEventListener('resize', updateDropdownPositions)
})

// Handle suggestion click
const handleSuggestionClick = (suggestion: SearchSuggestion) => {
  router.push({
    name: 'listing-detail',
    params: { id: suggestion.object_id }
  })
}

// Handle input blur
const handleBlur = () => {
  // Small delay to allow click events on suggestions
  setTimeout(() => {
    showSuggestions.value = false
  }, 200)
}

// Force show suggestions when we have results and update dropdown position
watch(searchResults, (newResults) => {
  if (newResults.length > 0) {
    showSuggestions.value = true
    updateDropdownPositions()
  }
})

// Update dropdown positions when showing suggestions
watch(showSuggestions, (newValue) => {
  if (newValue) {
    updateDropdownPositions()
  }
})

// Update dropdown positions when showing location suggestions
watch(showLocationSuggestions, (newValue) => {
  if (newValue) {
    updateDropdownPositions()
  }
})
</script>

<style>
.bg-clip-text {
  -webkit-background-clip: text;
}
</style>
